
// Imports market prices from a Collectr CSV export

import { useState, useRef } from "react";
import { useTheme } from "./App";
import { addPriceHistory, updateInventoryItem } from "./supabase";
import CONFIG from "./config";

// ── PARSING HELPERS ───────────────────────────────────────────────────────────
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else { cur += line[i]; }
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/^"|"$/g, ''); });
    return obj;
  });
}

function getMarketPrice(row) {
  const key = Object.keys(row).find(k => k.startsWith("Market Price"));
  if (!key) return 0;
  return parseFloat(String(row[key]).replace(/,/g, '')) || 0;
}

function getCollectrDate(row) {
  const key = Object.keys(row).find(k => k.startsWith("Market Price"));
  if (!key) return new Date().toISOString();
  const m = key.match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] + "T00:00:00Z" : new Date().toISOString();
}

// ── MATCHING ENGINE ───────────────────────────────────────────────────────────

// Normalize card number: "025/088" → "25", "TG15/TG30" → "TG15", "068" → "68"
function normalizeNumber(n) {
  if (!n) return "";
  const base = String(n).split("/")[0].trim();
  // Keep letters (TG, GG etc) but strip leading zeros from numeric part
  if (/^[A-Za-z]/.test(base)) return base.toUpperCase();
  return String(parseInt(base, 10) || base);
}

// Extract base card name: strip " - 067", [Staff], (Full Art), (JP) etc
function extractBaseName(name) {
  let n = name;
  n = n.replace(/\s*-\s*\d+[A-Za-z]*$/, '');   // " - 067" suffix
  n = n.replace(/\s*\[[^\]]+\]/g, '');           // [Staff] etc
  n = n.replace(/\s*\([^)]+\)/g, '');            // (Full Art), (JP) etc
  return n.trim().toLowerCase();
}

// Resolve a Collectr set name → your config set name
function resolveSetName(collectrSet) {
  if (!collectrSet) return null;
  // Direct mapping first
  const mapped = CONFIG.COLLECTR_SET_MAP?.[collectrSet];
  if (mapped !== undefined) return mapped; // null means "review queue"
  // Fall through to exact match
  return collectrSet;
}

// Normalize set for loose comparison
function normalizeSet(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
}

// Main matching function — returns { match, tier, resolvedSet }
function findMatch(row, inventory) {
  const csvRaw    = row["Set"] || "";
  const csvNumber = normalizeNumber(row["Card Number"]);
  const csvBase   = extractBaseName(row["Product Name"] || "");

  const resolvedSet = resolveSetName(csvRaw);

  // null resolvedSet = unmatchable set (Miscellaneous etc)
  if (resolvedSet === null) {
    return { match: null, tier: "unmatchable", resolvedSet };
  }

  const targetSet  = normalizeSet(resolvedSet);
  const fallbackSet = normalizeSet(csvRaw);

  // Tier 1: number + resolved set (exact)
  if (csvNumber) {
    const t1 = inventory.find(i => {
      const iNum = normalizeNumber(i.Number);
      const iSet = normalizeSet(i.Set);
      return iNum === csvNumber && (iSet === targetSet || iSet === fallbackSet);
    });
    if (t1) return { match: t1, tier: 1, resolvedSet };
  }

  // Tier 2: number + loose set (strip "promos", "trainer gallery" etc)
  if (csvNumber) {
    const targetCore = targetSet.replace(/(trainer gallery|shiny vault|galarian gallery|classic collection|promos?|base set)/g, '').trim();
    const t2 = inventory.find(i => {
      const iNum  = normalizeNumber(i.Number);
      const iCore = normalizeSet(i.Set).replace(/(trainer gallery|shiny vault|galarian gallery|classic collection|promos?|base set)/g, '').trim();
      return iNum === csvNumber && iCore === targetCore && targetCore.length > 2;
    });
    if (t2) return { match: t2, tier: 2, resolvedSet };
  }

  // Tier 3: base name + set
  {
    const t3 = inventory.find(i => {
      const iName = (i.CardName || "").toLowerCase().trim();
      const iSet  = normalizeSet(i.Set);
      return iName === csvBase && (iSet === targetSet || iSet === fallbackSet);
    });
    if (t3) return { match: t3, tier: 3, resolvedSet };
  }

  // No match
  return { match: null, tier: null, resolvedSet };
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PriceRefresh({ inventory, onClose, onComplete, vendorRates }) {
  const { S, T } = useTheme();
  const [step, setStep]         = useState("upload");
  const [matches, setMatches]   = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [progress, setProgress] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [done, setDone]         = useState(null);
  const [autoListPrice, setAutoListPrice] = useState(false);
  const fileRef = useRef(null);

  const markupRate = vendorRates?.markupRate ?? 1.25;

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCsv(e.target.result).filter(r =>
        r["Product Name"] && r["Watchlist"]?.toLowerCase() !== "true"
      );

      const matched   = [];
      const noMatch   = [];
      const recordedAt = getCollectrDate(rows[0] || {});

      rows.forEach(row => {
        const price = getMarketPrice(row);
        if (!price) return;

        const { match, tier, resolvedSet } = findMatch(row, inventory);

        if (match) {
          const oldPrice = match.MarketValueAtAcquisition || 0;
          matched.push({
            // For price_history insert
            inventory_id: match.ID,
            card_name:    match.CardName,
            set_name:     match.Set,
            card_number:  match.Number,
            market_value: price,
            source:       "collectr_import",
            recorded_at:  recordedAt,
            // For inventory update
            _invID:       match.ID,
            _newMarket:   Math.round(price),
            _newListPrice: Math.round(price * markupRate),
            // For display
            _csvName:     row["Product Name"],
            _matchedName: match.CardName,
            _oldPrice:    oldPrice,
            _newPrice:    price,
            _change:      price - oldPrice,
            _tier:        tier,
            _resolvedSet: resolvedSet,
          });
        } else {
          noMatch.push({
            name:   row["Product Name"],
            set:    row["Set"],
            number: row["Card Number"],
            price,
            tier,
            resolvedSet,
          });
        }
      });

      setMatches(matched);
      setUnmatched(noMatch);
      setStep("preview");
    };
    reader.readAsText(file);
  };

  const runUpdate = async () => {
    setUpdating(true);
    setStep("updating");
    try {
      const BATCH = 50;

      // 1. Insert price history
      for (let i = 0; i < matches.length; i += BATCH) {
        await addPriceHistory(matches.slice(i, i + BATCH));
        setProgress(Math.round(((i + BATCH) / matches.length) * 50));
      }

      // 2. Update market value (and optionally list price) on inventory items
      for (let i = 0; i < matches.length; i++) {
        const m = matches[i];
        const updates = { MarketValueAtAcquisition: m._newMarket };
        if (autoListPrice) {
          updates.ListPrice = m._newListPrice;
          // Also recalculate min sell price
          if (m._newListPrice) {
            updates.MinSellPrice = Math.round(m._newListPrice * 0.9);
          }
        }
        await updateInventoryItem(m._invID, updates);
        setProgress(50 + Math.round((i / matches.length) * 50));
      }

      setDone({ updated: matches.length, skipped: unmatched.length, listUpdated: autoListPrice });
      setStep("done");
      onComplete && onComplete(matches);
    } catch(e) {
      console.error("Price refresh error:", e);
      setStep("preview");
    }
    setUpdating(false);
  };

  const priceUp   = matches.filter(m => m._change > 0.01);
  const priceDown = matches.filter(m => m._change < -0.01);
  const tier1     = matches.filter(m => m._tier === 1);
  const tier2     = matches.filter(m => m._tier === 2);
  const tier3     = matches.filter(m => m._tier === 3);

  // ── UPLOAD ────────────────────────────────────────────────────────────────
  if (step === "upload") return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:600}}>Refresh Market Prices</div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{...S.infoBox,marginBottom:16}}>
          <div style={{fontSize:12,color:T.textMuted,lineHeight:1.8}}>
            <div>1. Export your collection from Collectr as CSV</div>
            <div>2. Upload here — prices matched by card number + set</div>
            <div>3. Market values update on matched cards automatically</div>
            <div>4. Optionally auto-calculate list price using your markup rate</div>
          </div>
        </div>

        {/* Auto list price toggle */}
        <div style={{...S.infoBox,marginBottom:16,display:"flex",
          justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600}}>Auto-update list price</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
              Set list price = market × {(markupRate*100).toFixed(0)}% markup
            </div>
          </div>
          <button
            style={{padding:"6px 16px",borderRadius:20,fontSize:12,fontWeight:700,
              border:"none",cursor:"pointer",
              background:autoListPrice?"#16a34a":"#374151",
              color:autoListPrice?"#fff":"#9ca3af"}}
            onClick={()=>setAutoListPrice(v=>!v)}>
            {autoListPrice ? "ON" : "OFF"}
          </button>
        </div>

        <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}}
          onChange={e => handleFile(e.target.files[0])} />
        <button style={{...S.btn("primary"),width:"100%",padding:"13px"}}
          onClick={() => fileRef.current?.click()}>
          Upload Collectr CSV
        </button>
      </div>
    </div>
  );

  // ── UPDATING ──────────────────────────────────────────────────────────────
  if (step === "updating") return (
    <div style={S.overlay}>
      <div style={{...S.drawer,textAlign:"center",padding:"40px 24px"}}>
        <div style={{fontSize:32,marginBottom:12}}>📈</div>
        <div style={{fontSize:16,fontWeight:600,marginBottom:8}}>Updating Prices…</div>
        <div style={{height:8,borderRadius:4,background:T.border,overflow:"hidden",marginBottom:8}}>
          <div style={{height:"100%",borderRadius:4,background:"#3b82f6",
            width:`${progress}%`,transition:"width 0.3s"}} />
        </div>
        <div style={{fontSize:12,color:T.textFaint}}>{progress}%</div>
      </div>
    </div>
  );

  // ── DONE ──────────────────────────────────────────────────────────────────
  if (step === "done") return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{...S.drawer,textAlign:"center",padding:"32px 24px"}}>
        <div style={{fontSize:40,marginBottom:12}}>✅</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:16}}>Prices Updated</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
          <div style={S.stat}><div style={S.statLbl}>Updated</div><div style={{...S.statVal,color:"#4ade80"}}>{done.updated}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Skipped</div><div style={{...S.statVal,color:T.textMuted}}>{done.skipped}</div></div>
          <div style={S.stat}><div style={S.statLbl}>History</div><div style={{...S.statVal,color:"#3b82f6"}}>+{done.updated}</div></div>
        </div>
        {done.listUpdated && (
          <div style={{...S.infoBox,marginBottom:16,textAlign:"left"}}>
            <div style={{fontSize:12,color:"#4ade80"}}>
              ✓ List prices auto-updated at {(markupRate*100).toFixed(0)}% markup
            </div>
          </div>
        )}
        <button style={{...S.btn("primary"),width:"100%"}} onClick={onClose}>Done</button>
      </div>
    </div>
  );

  // ── PREVIEW ───────────────────────────────────────────────────────────────
  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{...S.drawer,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:16,flexShrink:0}}>
          <div style={{fontSize:16,fontWeight:600}}>Price Refresh Preview</div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>

        {/* Match quality summary */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:12,flexShrink:0}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Matched</div>
            <div style={{...S.statVal,color:"#4ade80"}}>{matches.length}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>↑ Up</div>
            <div style={{...S.statVal,color:"#4ade80"}}>{priceUp.length}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>↓ Down</div>
            <div style={{...S.statVal,color:"#f87171"}}>{priceDown.length}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>No Match</div>
            <div style={{...S.statVal,color:T.textMuted}}>{unmatched.length}</div>
          </div>
        </div>

        {/* Confidence breakdown */}
        <div style={{...S.infoBox,marginBottom:12,flexShrink:0}}>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:6}}>Match confidence</div>
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:"#4ade80"}}>
              ● Tier 1 (exact): {tier1.length}
            </span>
            <span style={{fontSize:11,color:"#fbbf24"}}>
              ● Tier 2 (set alias): {tier2.length}
            </span>
            <span style={{fontSize:11,color:"#f87171"}}>
              ● Tier 3 (name only): {tier3.length}
            </span>
          </div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>
            Hit rate: {matches.length}/{matches.length + unmatched.length} ({Math.round(matches.length/(matches.length+unmatched.length||1)*100)}%)
          </div>
        </div>

        {/* Auto list price toggle */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:12,padding:"8px 12px",borderRadius:8,background:T.surface2,
          border:`1px solid ${T.border}`,flexShrink:0}}>
          <div>
            <div style={{fontSize:12,fontWeight:600}}>Auto-update list price</div>
            <div style={{fontSize:10,color:T.textMuted}}>
              Market × {(markupRate*100).toFixed(0)}% = new list price
              {autoListPrice && matches.length > 0 && (
                <span style={{color:"#4ade80",marginLeft:6}}>
                  e.g. ${matches[0]._newMarket.toFixed(0)} → ${matches[0]._newListPrice.toFixed(0)}
                </span>
              )}
            </div>
          </div>
          <button
            style={{padding:"5px 14px",borderRadius:16,fontSize:12,fontWeight:700,
              border:"none",cursor:"pointer",
              background:autoListPrice?"#16a34a":"#374151",
              color:autoListPrice?"#fff":"#9ca3af"}}
            onClick={()=>setAutoListPrice(v=>!v)}>
            {autoListPrice ? "ON" : "OFF"}
          </button>
        </div>

        {/* Matched cards */}
        <div style={{flex:1,overflowY:"auto",marginBottom:12}}>
          {matches.slice(0,100).map((m,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:"7px 0",
              borderBottom:`0.5px solid ${T.border}`}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:500}}>{m._matchedName}</div>
                <div style={{fontSize:10,color:T.textMuted}}>
                  {m.set_name}
                  {m._tier > 1 && (
                    <span style={{marginLeft:6,color:"#fbbf24",fontSize:9}}>
                      T{m._tier}
                    </span>
                  )}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <span style={{fontSize:11,color:T.textFaint}}>${Math.round(m._oldPrice)}</span>
                <span style={{fontSize:11,color:T.textMuted}}>→</span>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:12,fontWeight:600,
                    color:m._change>0?"#4ade80":m._change<0?"#f87171":T.textMuted}}>
                    ${m._newMarket}
                    {m._change !== 0 && (
                      <span style={{fontSize:10,marginLeft:4}}>
                        ({m._change>0?"+":""}{Math.round(m._change)})
                      </span>
                    )}
                  </div>
                  {autoListPrice && (
                    <div style={{fontSize:9,color:"#88bdf2"}}>
                      list → ${m._newListPrice}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {matches.length > 100 && (
            <div style={{fontSize:11,color:T.textFaint,textAlign:"center",padding:"8px 0"}}>
              +{matches.length-100} more matched
            </div>
          )}

          {/* Unmatched section */}
          {unmatched.length > 0 && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:11,fontWeight:600,color:"#fbbf24",marginBottom:6}}>
                {unmatched.length} unmatched — set not in config or no card found
              </div>
              {unmatched.map((c,i)=>(
                <div key={i} style={{padding:"5px 0",
                  borderBottom:`0.5px solid ${T.border}`}}>
                  <div style={{fontSize:11,color:T.textMuted}}>
                    {c.name}
                    <span style={{color:"#fbbf24",marginLeft:6}}>${c.price.toFixed(2)}</span>
                  </div>
                  <div style={{fontSize:10,color:T.textFaint}}>
                    {c.set}
                    {c.tier==="unmatchable" && (
                      <span style={{marginLeft:6,color:"#ef4444"}}>· unmatchable set</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button style={{...S.btn("primary"),width:"100%",padding:"13px",flexShrink:0}}
          onClick={runUpdate} disabled={matches.length === 0 || updating}>
          Update {matches.length} Card Prices
          {autoListPrice && " + List Prices"}
        </button>
      </div>
    </div>
  );
}

// ── PRICE SPARKLINE ───────────────────────────────────────────────────────────
export function PriceSparkline({ history, currentPrice }) {
  if (!history || history.length < 2) return null;

  const sorted = [...history].sort((a,b) => new Date(a.recorded_at) - new Date(b.recorded_at));
  const last10 = sorted.slice(-10);
  const prices = last10.map(h => parseFloat(h.market_value));
  const min    = Math.min(...prices);
  const max    = Math.max(...prices);
  const range  = max - min || 1;

  const W = 80, H = 24;
  const points = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * W;
    const y = H - ((p - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  }).join(" ");

  const first = prices[0];
  const last  = prices[prices.length - 1];
  const trend = last > first ? "#4ade80" : last < first ? "#f87171" : "#88bdf2";

  const now     = Date.now();
  const lastDate = new Date(last10[last10.length-1].recorded_at);
  const daysAgo  = Math.round((now - lastDate.getTime()) / 86400000);
  const label    = daysAgo === 0 ? "today" : `${daysAgo}d ago`;

  return (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <svg width={W} height={H} style={{flexShrink:0}}>
        <polyline points={points} fill="none" stroke={trend} strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={W} cy={H - ((last-min)/range)*(H-4) - 2} r="2.5" fill={trend} />
      </svg>
      <div style={{fontSize:10,color:trend,fontFamily:"'DM Mono',monospace",
        lineHeight:1.2,flexShrink:0}}>
        <div>{last > first ? "↑" : last < first ? "↓" : "→"} ${last.toFixed(0)}</div>
        <div style={{opacity:0.7}}>{label}</div>
      </div>
    </div>
  );
}
